import { Card, IconButton, Typography, useTheme } from '@mui/material';
import { IconLibrary } from '../../../display/IconLibrary/IconLibrary';
import { IconName } from '../../../display/IconLibrary/iconLibraryTypes';
import { FlexContainer } from '../../ContentContainers/FlexContainer';
import { Box } from '../../Box/Box';

/** Bullet used between inline resource metadata segments. */
const METADATA_SEPARATOR = ' • ';

/**
 * Returns whether a metadata string should be rendered.
 *
 * @param value - Optional segment value.
 * @returns True when the value is a non-empty string.
 */
function isPresent(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/**
 * Formats two optional values on one line, inserting a bullet only when both are present.
 *
 * @param value1 - Leading segment.
 * @param value2 - Trailing segment.
 * @returns A single formatted line (may be empty when both values are absent).
 */
function getLineWithSeparator(value1?: string, value2?: string): string {
  const first = isPresent(value1) ? value1 : '';
  const second = isPresent(value2) ? value2 : '';
  const separator =
    isPresent(value1) && isPresent(value2) ? METADATA_SEPARATOR : '';

  return `${first}${separator}${second}`;
}

/**
 * Formats recognizable US phone numbers with dashes for the resource card.
 *
 * @param value - Raw phone number from CBO/cache/API state.
 * @returns `999-999-9999` for US numbers, otherwise the original value.
 */
function formatResourceCardPhone(value: string | undefined): string | undefined {
  if (!isPresent(value)) {
    return value;
  }

  const digits = value.replace(/\D/g, '');
  const nationalDigits =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (nationalDigits.length !== 10) {
    return value;
  }

  return `${nationalDigits.slice(0, 3)}-${nationalDigits.slice(
    3,
    6
  )}-${nationalDigits.slice(6)}`;
}

export interface ResourceCardProps {
  /** Organization or resource display name. */
  name?: string;
  /** Category label shown after the name (e.g. "Food Pantry"). */
  category?: string;
  /** Date string displayed in parentheses on the header row. */
  dateLabel?: string;
  /** Resource contact person. */
  contactName?: string;
  /** Contact phone number. */
  phone?: string;
  /** Website hostname or URL; rendered as an underlined external link. */
  website?: string;
  /** Street address line. */
  address?: string;
  /** IconLibrary icon; defaults to `ListMagnifyingGlass`. */
  iconName?: IconName;
  /** When set, the card is clickable (e.g. recent-resource picker). */
  onClick?: () => void;
  /** Optional edit action displayed in the card header area. */
  onEdit?: () => void;
  /** Optional remove/reset action displayed in the card header area. */
  onRemove?: () => void;
}

/**
 * Builds an absolute URL when the website value omits a protocol.
 *
 * @param website - Hostname or full URL from the resource record.
 * @returns A value suitable for an anchor `href`.
 */
function toWebsiteHref(website: string): string {
  if (/^https?:\/\//i.test(website)) {
    return website;
  }

  return `https://${website}`;
}

/**
 * Horizontal card for community resources: name, category, contact details, and a
 * large decorative IconLibrary glyph cropped on the right.
 *
 * @param props - Resource metadata and optional icon override.
 * @returns Outlined card matching the light-mode resource card pattern.
 */
export const ResourceCard = ({
  name,
  category,
  dateLabel,
  contactName,
  phone,
  website,
  address,
  iconName = 'ListMagnifyingGlass',
  onClick,
  onEdit,
  onRemove,
}: ResourceCardProps) => {
  const theme = useTheme();
  const textColor = theme.palette.primary.main;
  const titleLine = getLineWithSeparator(name, category);
  const displayPhone = formatResourceCardPhone(phone);
  const contactLine = getLineWithSeparator(displayPhone, website);
  const hasHeader = isPresent(titleLine) || isPresent(dateLabel);
  const hasContactName = isPresent(contactName);
  const hasContact = isPresent(contactLine);
  const hasActions = Boolean(onEdit || onRemove);

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: theme.palette.common.white,
        ...(onClick && { cursor: 'pointer' }),
      }}
    >
      {hasActions && (
        <Box
          position="absolute"
          top="10px"
          right="12px"
          zIndex={1}
          display="flex"
          gap="4px"
        >
          {onEdit && (
            <IconButton
              size="small"
              aria-label="Edit resource"
              onClick={event => {
                event.stopPropagation();
                onEdit();
              }}
            >
              <IconLibrary
                iconName="PencilSimple"
                weight="regular"
                size={20}
                color="primary"
              />
            </IconButton>
          )}
          {onRemove && (
            <IconButton
              size="small"
              aria-label="Remove resource"
              onClick={event => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <IconLibrary
                iconName="X"
                weight="regular"
                size={20}
                color="primary"
              />
            </IconButton>
          )}
        </Box>
      )}
      <FlexContainer
        direction="row"
        alignItems="stretch"
        justifyContent="space-between"
        height="fit-content"
        overflow="hidden"
        overflowX="hidden"
        overflowY="hidden"
      >
        <FlexContainer
          direction="column"
          flexGrow={1}
          flexBasis="0"
          minWidth="0"
          gap={1}
          padding="16px"
          height="fit-content"
          overflow="visible"
          overflowX="visible"
          overflowY="visible"
          sx={{ color: textColor }}
        >
          {hasHeader && (
            <Typography
              variant="labelLarge"
              component="div"
              sx={{ fontWeight: theme.app.fontWeight.bold }}
            >
              {titleLine}
              {isPresent(dateLabel) && (
                <>
                  {isPresent(titleLine) && ' '}
                  <Typography
                    variant="bodyMedium"
                    component="span"
                    sx={{ fontWeight: theme.app.fontWeight.regular }}
                  >
                    ({dateLabel})
                  </Typography>
                </>
              )}
            </Typography>
          )}

          {hasContactName && (
            <Typography variant="bodyMedium" component="p" sx={{ m: 0 }}>
              Contact: {contactName}
            </Typography>
          )}

          {hasContact && (
            <Typography variant="bodyMedium" component="p" sx={{ m: 0 }}>
              {!isPresent(website) ? (
                contactLine
              ) : (
                <>
                  {getLineWithSeparator(displayPhone)}
                  {isPresent(displayPhone) && METADATA_SEPARATOR}
                  <Typography
                    variant="bodyMedium"
                    component="a"
                    href={toWebsiteHref(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    {website}
                  </Typography>
                </>
              )}
            </Typography>
          )}

          {isPresent(address) && (
            <Typography variant="bodyMedium" component="p" sx={{ m: 0 }}>
              {address}
            </Typography>
          )}
        </FlexContainer>

        <Box
          position="relative"
          flexShrink={0}
          width="140px"
          minWidth="140px"
          overflow="hidden"
          alignSelf="stretch"
        >
          <IconLibrary
            iconName={iconName}
            weight="regular"
            size={110}
            color="primary"
            style={{
              position: 'absolute',
              right: -16,
              bottom: -20,
            }}
            mirrored
          />
        </Box>
      </FlexContainer>
    </Card>
  );
};
